import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, EMPTY, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { CustomModalsService } from '../services/custom-modals.service';

/**
 * Globally handles HTTP 428 Precondition Required responses that carry
 * { code: 'CONFIRM_REQUIRED', message } from em3.api. Shows a confirmation
 * prompt with the server's message, and on CONFIRM retries the original
 * request with the CONFIRM-DELETE header. On dismiss, the observable
 * completes silently so call sites don't surface a generic error toast.
 *
 * Any other 428 (with a different `code` or no body) and all other status
 * codes pass through unchanged.
 */
@Injectable()
export class ConfirmationInterceptor implements HttpInterceptor {

    constructor(private _modals: CustomModalsService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status !== 428 || error.error?.code !== 'CONFIRM_REQUIRED') {
                    // RxJS 6: throwError takes the error directly, NOT a
                    // factory. The factory syntax wraps the error in a
                    // function which downstream interceptors (e.g.
                    // AuthHttpIntercepter checking error.status) cannot
                    // unwrap -- and AuthHttpIntercepter's 401 refresh
                    // path was silently bypassed for the entire app.
                    return throwError(error);
                }

                return from(this._modals.showPrompt(
                    error.error.message,
                    'Confirmation Required',
                    "Please type 'CONFIRM'",
                    'CONFIRM'
                )).pipe(
                    switchMap(res => {
                        if (res !== 'CONFIRM') {
                            return EMPTY;
                        }
                        const retried = request.clone({
                            setHeaders: { 'CONFIRM-DELETE': 'CONFIRM' }
                        });
                        return next.handle(retried);
                    }),
                    catchError(() => EMPTY)
                );
            })
        );
    }
}
